export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3
}

export interface LogEntry {
  level: LogLevel;
  message: string;
  data?: unknown;
  timestamp: Date;
  source?: string;
}

export class LoggerService {
  private static instance: LoggerService;
  private static isDevelopment = import.meta.env?.MODE === 'development';
  private static currentLogLevel = LoggerService.isDevelopment ? LogLevel.DEBUG : LogLevel.WARN;

  private constructor () {}

  public static getInstance (): LoggerService {
    if (!LoggerService.instance) {
      LoggerService.instance = new LoggerService();
    }
    return LoggerService.instance;
  }

  /**
   * Log debug information (only in development)
   */
  static debug (message: string, data?: unknown, source?: string): void {
    if (this.currentLogLevel <= LogLevel.DEBUG) {
      this.writeLog(LogLevel.DEBUG, message, data, source);
    }
  }

  /**
   * Log general information
   */
  static info (message: string, data?: unknown, source?: string): void {
    if (this.currentLogLevel <= LogLevel.INFO) {
      this.writeLog(LogLevel.INFO, message, data, source);
    }
  }

  /**
   * Log warnings
   */
  static warn (message: string, data?: unknown, source?: string): void {
    if (this.currentLogLevel <= LogLevel.WARN) {
      this.writeLog(LogLevel.WARN, message, data, source);
    }
  }

  /**
   * Log errors (always logged)
   */
  static error (message: string, error?: unknown, source?: string): void {
    this.writeLog(LogLevel.ERROR, message, error, source);
  }

  /**
   * Log API calls for debugging
   */
  static api (endpoint: string, method: string, data?: unknown): void {
    this.debug(`API ${method.toUpperCase()}: ${endpoint}`, data, 'API');
  }

  /**
   * Log user actions for analytics
   */
  static userAction (action: string, data?: unknown): void {
    this.info(`User Action: ${action}`, data, 'USER');
  }

  private static writeLog (level: LogLevel, message: string, data?: unknown, source?: string): void {
    const logEntry: LogEntry = {
      level,
      message,
      data,
      timestamp: new Date(),
      source
    };

    // في الإنتاج، يمكن إرسال الأخطاء إلى خدمة monitoring
    if (!this.isDevelopment && level === LogLevel.ERROR) {
      this.sendToMonitoring(logEntry);
    }
  }

  private static sendToMonitoring (_logEntry: LogEntry): void {
    // TODO: تطبيق إرسال الأخطاء إلى خدمة monitoring
    // مثل Sentry, LogRocket, أو خدمة مخصصة
  }

  /**
   * Clear all console logs (development only)
   */
  static clear (): void {
    if (this.isDevelopment) {
    }
  }

  /**
   * Set log level dynamically
   */
  static setLogLevel (level: LogLevel): void {
    this.currentLogLevel = level;
  }
}
